import { useQuery } from "@apollo/client";
import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  ArtifactType,
  GetGoalOverviewSelectedEntityQueryQuery,
  GetGoalOverviewSelectedEntityQueryQueryVariables,
  GoalScope,
  GoalState,
} from "types/graphql-schema";
import { DateRangeEnum } from "types/topicflow";

import { getExplorerFiltersUrl } from "@apps/explorer/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import InputDatePicker from "@components/input/input-datepicker";
import { useLink } from "@components/link/link-helpers";
import { Select, SelectOption } from "@components/select/select";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxOrgOption,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import { dateRangeToDateArray, isBackgroundLocation } from "@helpers/helpers";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import GoalOverviewFavouriteGoals from "./favourite-goals";
import GoalOverviewGoalListStats from "./goal-list-stats";
import getGoalOverviewSelectedEntityQuery from "./graphql/get-goal-overview-selected-entity-query";
import GoalPageHeader from "./header";
import {
  OverviewDateRangeType,
  OverviewExtraDateRangeEnum,
} from "./page-constants";
import GoalOverviewRelatedEntities from "./related-entities";

const separatorCustomDateRange = "_";

const isValidCustomDateRange = (dateRange: string) => {
  const [startDateStr, endDateStr] = dateRange.split(separatorCustomDateRange);
  const startDate = moment(startDateStr, "YYYY-MM-DD", true);
  const endDate = moment(endDateStr, "YYYY-MM-DD", true);
  return (
    startDate.isValid() &&
    endDate.isValid() &&
    moment(startDate).isBefore(moment(endDate))
  );
};

const GoalOverview = () => {
  const label = useLabel();
  const link = useLink();
  const location = useLocation();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  useDocumentTitle(`${label("goal", { capitalize: true })} overview`);
  const { id, type } = useParams<{
    id?: string;
    type?: UserComboboxOptionType;
  }>();
  const queryParams = useUrlQueryParams<{
    dateRange: DateRangeEnum | string;
  }>({
    dateRange: DateRangeEnum.thisQuarter,
  });

  const currentUserOption = {
    id: currentUser.id,
    name: currentUser.name,
    type: UserComboboxOptionType.USER,
    avatar: currentUser.avatar,
    email: currentUser.email,
  } as UserComboboxUserOption;
  const currentOrgOption = {
    id: currentOrganization.id,
    name: currentOrganization.name,
    type: UserComboboxOptionType.ORG,
  } as UserComboboxOrgOption;
  const defaultSelected =
    id === undefined || type === undefined
      ? currentUserOption
      : id === String(currentUser.id) && type === UserComboboxOptionType.USER
        ? currentUserOption
        : id === String(currentOrganization.id) &&
            type === UserComboboxOptionType.ORG
          ? currentOrgOption
          : null;

  const [selected, setSelected] = useState<UserComboboxOption | null>(
    defaultSelected,
  );
  const queryDateRangeIsDateRangeEnum = Object.values(DateRangeEnum).includes(
    queryParams.dateRange as DateRangeEnum,
  );
  const queryDateRangeIsValidCustom =
    !queryDateRangeIsDateRangeEnum &&
    isValidCustomDateRange(queryParams.dateRange);

  const [selectedDateRange, setSelectedDateRange] =
    useState<OverviewDateRangeType>(
      queryDateRangeIsDateRangeEnum
        ? (queryParams.dateRange as DateRangeEnum)
        : queryDateRangeIsValidCustom
          ? OverviewExtraDateRangeEnum.custom
          : DateRangeEnum.thisQuarter,
    );

  const [customDateRange, setCustomDateRange] = useState<string[]>(
    queryDateRangeIsValidCustom
      ? queryParams.dateRange.split(separatorCustomDateRange)
      : dateRangeToDateArray({
          range: DateRangeEnum.thisQuarter,
          quarterStartMonth: currentOrganization.quarterStartMonth,
        }),
  );

  const userId = type === UserComboboxOptionType.USER && id ? parseInt(id) : -1;
  const teamId = type === UserComboboxOptionType.TEAM && id ? parseInt(id) : -1;
  const organizationId =
    type === UserComboboxOptionType.ORG && id ? parseInt(id) : -1;
  const { data } = useQuery<
    GetGoalOverviewSelectedEntityQueryQuery,
    GetGoalOverviewSelectedEntityQueryQueryVariables
  >(getGoalOverviewSelectedEntityQuery, {
    variables: {
      userId: userId,
      hasUserId: userId > 0,
      teamId: teamId,
      hasTeamId: teamId > 0,
      organizationId: organizationId,
      hasOrganizationId: organizationId > 0,
    },
    onCompleted: (response) => {
      if (response.user) {
        setSelected({
          id: response.user.id,
          name: response.user.name,
          type: UserComboboxOptionType.USER,
          avatar: response.user.avatar,
          email: response.user.email,
        });
      } else if (response.team) {
        setSelected({
          id: response.team.id,
          title: response.team.title,
          type: UserComboboxOptionType.TEAM,
        });
      } else if (response.organization) {
        setSelected({
          id: response.organization.id,
          name: response.organization.name,
          type: UserComboboxOptionType.ORG,
        });
      }
    },
  });

  const goalsCount =
    selected?.type === UserComboboxOptionType.ORG
      ? (data?.organizationArtifacts?.totalCount ?? 0)
      : selected?.type === UserComboboxOptionType.TEAM
        ? (data?.teamArtifacts?.totalCount ?? 0)
        : (data?.userArtifacts?.totalCount ?? 0);

  const { options, setQuery, query } = useUserComboboxQuery({
    types: [
      UserComboboxOptionType.ORG,
      UserComboboxOptionType.TEAM,
      UserComboboxOptionType.USER,
    ],
    selected: selected ? selected : undefined,
  });

  const dateRangeOptions: SelectOption<OverviewDateRangeType>[] = Object.values(
    DateRangeEnum,
  ).map((dateRange) => ({
    value: dateRange,
    label: label(dateRange, { capitalize: true }),
    selected: selectedDateRange === dateRange,
    description: dateRangeToDateArray({
      range: dateRange,
      quarterStartMonth: currentOrganization.quarterStartMonth,
    })
      .map((date) => moment(date).format("ll"))
      .join(" - "),
  }));
  const allDateRangeOptions = [
    ...dateRangeOptions,
    {
      value: OverviewExtraDateRangeEnum.custom,
      label: label(OverviewExtraDateRangeEnum.custom, { titleCase: true }),
      selected: selectedDateRange === OverviewExtraDateRangeEnum.custom,
    },
  ];

  const goalDueBetweenDates =
    selectedDateRange === OverviewExtraDateRangeEnum.custom
      ? customDateRange
      : dateRangeToDateArray({
          range: selectedDateRange,
          quarterStartMonth: currentOrganization.quarterStartMonth,
        });

  const handleChangeDateRange = (
    option: SelectOption<OverviewDateRangeType>,
  ) => {
    setCustomDateRange(goalDueBetweenDates);
    setSelectedDateRange(option.value);
  };

  useEffect(() => {
    if (selected?.id && selected?.type && isBackgroundLocation(location)) {
      const search = new URLSearchParams(location.search);
      if (selectedDateRange === OverviewExtraDateRangeEnum.custom) {
        search.set("dateRange", customDateRange.join("_"));
      } else {
        search.set("dateRange", selectedDateRange);
      }
      link.replace({
        pathname: `/goals/${selected.type}/${selected.id}`,
        search: search.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, selectedDateRange, customDateRange]);

  return (
    <div aria-label="Goals" className="flex flex-col flex-1 w-full">
      <GoalPageHeader selected={selected} />
      <div className="mx-auto max-w-7xl w-full">
        <div className="p-6 flex flex-col gap-8">
          <div className="flex items-center gap-6 justify-between">
            <div className="flex items-center gap-4">
              <UserCombobox
                options={options}
                value={selected}
                onChangeValue={setSelected}
                query={query}
                onChangeQuery={setQuery}
                placeholder="Loading..."
              />
              <Select<OverviewDateRangeType>
                options={allDateRangeOptions}
                value={selectedDateRange}
                onChange={handleChangeDateRange}
              />
              {selectedDateRange === OverviewExtraDateRangeEnum.custom &&
                customDateRange && (
                  <div className="flex items-center gap-1">
                    <InputDatePicker
                      date={moment(customDateRange[0]).toDate()}
                      maxDate={moment(customDateRange[1]).toDate()}
                      isClearable={false}
                      className="w-36"
                      onChangeDate={(date) => {
                        if (date) {
                          setCustomDateRange([
                            moment(date).format("YYYY-MM-DD"),
                            customDateRange[1],
                          ]);
                        }
                      }}
                    />
                    <TopicflowIcon
                      type="arrowRight"
                      className="text-gray-400 h-3 w-3"
                    />
                    <InputDatePicker
                      date={moment(customDateRange[1]).toDate()}
                      minDate={moment(customDateRange[0]).toDate()}
                      isClearable={false}
                      className="w-36"
                      onChangeDate={(date) => {
                        if (date) {
                          setCustomDateRange([
                            customDateRange[0],
                            moment(date).format("YYYY-MM-DD"),
                          ]);
                        }
                      }}
                    />
                  </div>
                )}
            </div>
            <div>
              <Button
                text={`View All Open ${label("goal", {
                  pluralize: true,
                  titleCase: true,
                })} (${goalsCount})`}
                theme={buttonTheme.lightBlue}
                to={getExplorerFiltersUrl({
                  type: ArtifactType.Goal,
                  goalState: [GoalState.Open, GoalState.Draft],
                  goalOwners:
                    selected?.type === UserComboboxOptionType.USER
                      ? selected.id
                      : null,
                  goalTeams:
                    selected?.type === UserComboboxOptionType.TEAM
                      ? [selected.id]
                      : null,
                  goalScope:
                    selected?.type === UserComboboxOptionType.TEAM
                      ? GoalScope.Team
                      : selected?.type === UserComboboxOptionType.ORG
                        ? GoalScope.Organization
                        : null,
                })}
                disabled={goalsCount === 0}
              />
            </div>
          </div>
          {selected && (
            <GoalOverviewGoalListStats
              selected={selected}
              selectedDateRange={selectedDateRange}
              goalDueBetweenDates={goalDueBetweenDates}
            />
          )}
          {selected && (
            <GoalOverviewRelatedEntities
              selected={selected}
              goalDueBetweenDates={goalDueBetweenDates}
            />
          )}
          {selected && (
            <GoalOverviewFavouriteGoals
              selected={selected}
              goalDueBetweenDates={goalDueBetweenDates}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalOverview;
